import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getProjects,
  Project,
  generateQuizConfig,
  updateQuizConfig,
  QuizConfig,
  getMasterplanStreams,
  getMasterplanArticles,
  MasterplanArticle
} from '../services/api';
import AIFieldGenerator from './AIFieldGenerator';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassInput } from './ui/GlassInput';
import { GlassSelect } from './ui/GlassSelect';
import { ModuleHeader } from './ui/ModuleHeader';
import './QuizCreator.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const QuizCreator: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [masterplanStreams, setMasterplanStreams] = useState<string[]>([]);
  const [articles, setArticles] = useState<MasterplanArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<MasterplanArticle | null>(null);

  const [formData, setFormData] = useState({
    project_id: '',
    stream: '',
    article_title: '',
    name: '',
    quiz_type: 'assessment',
    description: '',
    quiz_purpose: ''
  });

  const [generatedQuiz, setGeneratedQuiz] = useState<Partial<QuizConfig> | null>(null);
  const [leadMagnet, setLeadMagnet] = useState({
    lead_magnet_enabled: false,
    lead_magnet_url: '',
    lead_magnet_label: '',
  });

  // Helper to normalize stream names (remove leading numbers, newlines, parenthetical suffixes, extra whitespace)
  const normalizeStream = (s: string): string => {
    return s
      .replace(/^\d+[\.\)]\s*/, '')           // Remove leading numbers like "1)" or "1."
      .replace(/\s*\(.*?\)\s*/g, '')          // Remove parenthetical suffixes like "(Melbourne/VIC)"
      .replace(/\s+/g, ' ')                   // Normalize whitespace
      .trim();
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (formData.project_id) {
      loadStreams(formData.project_id);
    }
  }, [formData.project_id]);

  useEffect(() => {
    if (formData.project_id && formData.stream) {
      loadArticles(formData.project_id, formData.stream);
    } else {
      setArticles([]);
      setSelectedArticle(null);
    }
  }, [formData.project_id, formData.stream]);

  const loadProjects = async () => {
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const loadStreams = async (projectId: string) => {
    try {
      const response = await getMasterplanStreams(projectId);
      const normalizedStreams = (response.streams || []).map((s: string) => normalizeStream(s));
      setMasterplanStreams(normalizedStreams);
    } catch (err) {
      console.log('No masterplan streams available for this project');
      setMasterplanStreams([]);
    }
  };

  const loadArticles = async (projectId: string, stream: string) => {
    if (!stream) {
      setArticles([]);
      setSelectedArticle(null);
      return;
    }
    try {
      const normalizedStream = normalizeStream(stream);
      const articlesData = await getMasterplanArticles(projectId, normalizedStream);
      setArticles(articlesData);
    } catch (err) {
      console.log('No articles available for this stream');
      setArticles([]);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'article_title') {
      const article = articles.find(a => a.title === value);
      setSelectedArticle(article || null);
    }
  };

  const handleGenerate = async () => {
    if (!formData.project_id || !formData.name || !formData.quiz_purpose) {
      setError('Please fill in all required fields');
      return;
    }

    setIsGenerating(true);
    setError('');
    try {
      const quiz = await generateQuizConfig({
        project_id: formData.project_id,
        quiz_name: formData.name,
        quiz_purpose: formData.quiz_purpose,
        quiz_type: formData.quiz_type,
        context: {
          stream: formData.stream,
          article_content: selectedArticle?.prompt || ''
        }
      });
      setGeneratedQuiz(quiz);
      setStep(3);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to generate quiz'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedQuiz?.quiz_id) return;

    setIsSaving(true);
    setError('');
    try {
      // The generate endpoint already saved the quiz to the DB.
      // Update it with any step-3 edits (lead magnet, description, etc.)
      await updateQuizConfig(generatedQuiz.quiz_id, {
        name: formData.name,
        description: formData.description,
        quiz_type: formData.quiz_type,
        ...leadMagnet,
      });
      navigate('/quizzes');
    } catch (err: any) {
      setError(getApiError(err, 'Failed to save quiz'));
    } finally {
      setIsSaving(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Quiz Basics</h2>
              <div className="form-group">
                <GlassSelect
                  label="Project"
                  value={formData.project_id}
                  onChange={(value) => handleInputChange('project_id', value)}
                  options={[
                    { value: '', label: 'Select a Project' },
                    ...projects.map(p => ({ value: p.project_id, label: p.name }))
                  ]}
                />
              </div>

              {formData.project_id && (
                <>
                  <div className="form-group">
                    <GlassSelect
                      label="Content Stream"
                      value={formData.stream}
                      onChange={(value) => handleInputChange('stream', value)}
                      options={[
                        { value: '', label: 'Select a Stream' },
                        ...masterplanStreams.map(s => ({ value: s, label: s }))
                      ]}
                    />
                  </div>

                  {formData.stream && (
                    <div className="form-group">
                      <GlassSelect
                        label="Related Article"
                        value={formData.article_title}
                        onChange={(value) => handleInputChange('article_title', value)}
                        options={[
                          { value: '', label: 'Select an Article' },
                          ...articles.map(a => ({ value: a.title, label: a.title }))
                        ]}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <GlassInput
                  label="Quiz Name"
                  value={formData.name}
                  onChange={(value) => handleInputChange('name', value)}
                  placeholder="e.g. Estate Planning Risk Assessment"
                />
              </div>

              <div className="form-group">
                <GlassSelect
                  label="Quiz Type"
                  value={formData.quiz_type}
                  onChange={(value) => handleInputChange('quiz_type', value)}
                  options={[
                    { value: 'assessment', label: 'Assessment (Scored)' },
                    { value: 'personality', label: 'Personality (Categorized)' },
                    { value: 'knowledge', label: 'Knowledge (Right/Wrong)' }
                  ]}
                />
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  className="glass-textarea"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Internal notes or brief intro for the quiz"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-base)',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text)',
                    background: 'var(--panel)',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          </GlassCard>
        );

      case 2:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Quiz Content</h2>
              <div className="form-group">
                <label>What is the purpose of this quiz?</label>
                <textarea
                  className="glass-textarea"
                  value={formData.quiz_purpose}
                  onChange={(e) => handleInputChange('quiz_purpose', e.target.value)}
                  placeholder="e.g. Assess if Melbourne business owners have legally sound Wills..."
                  rows={5}
                  required
                  style={{
                    width: '100%',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-base)',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text)',
                    background: 'var(--panel)',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                />
                <AIFieldGenerator
                  fieldType="quiz_purpose"
                  projectId={formData.project_id}
                  currentValue={formData.quiz_purpose}
                  context={{
                    stream: formData.stream,
                    article_content: selectedArticle?.prompt || ''
                  }}
                  onAccept={(content) => handleInputChange('quiz_purpose', content)}
                  label="Quiz Purpose"
                />
                <small>Describe the goal, the target audience, and what you want to evaluate.</small>
              </div>

              <div className="action-area">
                <GlassButton
                  variant="primary"
                  onClick={handleGenerate}
                  disabled={isGenerating || !formData.quiz_purpose}
                  loading={isGenerating}
                >
                  {isGenerating ? '🪄 Generating Quiz...' : '✨ Generate Quiz with AI'}
                </GlassButton>
              </div>
            </div>
          </GlassCard>
        );

      case 3:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Review & Finalize</h2>
              {generatedQuiz && (
                <div className="quiz-preview">
                  <div className="preview-section">
                    <h3>Questions</h3>
                    <div className="questions-grid">
                      {generatedQuiz.questions?.map((q, idx) => (
                        <GlassCard key={idx} variant="default" padding="md" className="preview-q">
                          <strong>{idx + 1}. {q.question}</strong>
                          <div className="preview-opts">
                            {q.options?.map((o: any, oi: number) => (
                              <div key={oi} className="preview-opt">
                                <span>{o.label}</span>
                                <span className="opt-score">Score: {o.score}</span>
                              </div>
                            ))}
                          </div>
                        </GlassCard>
                      ))}
                    </div>
                  </div>

                  <div className="preview-section" style={{ marginTop: 'var(--space-6)' }}>
                    <h3>Scoring Logic</h3>
                    <div className="ranges-grid">
                      {generatedQuiz.scoring_logic?.ranges?.map((r: any, idx: number) => (
                        <GlassCard key={idx} variant="strong" padding="md" className="preview-range">
                          <div className="range-hdr">
                            <strong>{r.level}</strong>
                            <span>{r.min}-{r.max} pts</span>
                          </div>
                          <p>{r.message}</p>
                        </GlassCard>
                      ))}
                    </div>
                  </div>

                  <div className="preview-section" style={{ marginTop: 'var(--space-6)' }}>
                    <h3>Lead Magnet (optional)</h3>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                      Offer a downloadable resource to quiz completers who provide their email.
                    </p>
                    <div className="seo-field-group seo-field-group--checkbox" style={{ marginBottom: '10px' }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={leadMagnet.lead_magnet_enabled}
                          onChange={e => setLeadMagnet(f => ({ ...f, lead_magnet_enabled: e.target.checked }))}
                        />
                        <span>Enable lead magnet for this quiz</span>
                      </label>
                    </div>
                    {leadMagnet.lead_magnet_enabled && (
                      <>
                        <div className="form-group" style={{ marginBottom: '10px' }}>
                          <label className="form-label">Download URL</label>
                          <input
                            type="url"
                            className="form-input"
                            value={leadMagnet.lead_magnet_url}
                            onChange={e => setLeadMagnet(f => ({ ...f, lead_magnet_url: e.target.value }))}
                            placeholder="https://yourdomain.com/guide.pdf"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Button Label</label>
                          <input
                            type="text"
                            className="form-input"
                            value={leadMagnet.lead_magnet_label}
                            onChange={e => setLeadMagnet(f => ({ ...f, lead_magnet_label: e.target.value }))}
                            placeholder="Download Your Free Guide"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </GlassCard>
        );

      default:
        return null;
    }
  };

  return (
    <div className="quiz-creator">
      <ModuleHeader
        title="Create New Quiz"
        breadcrumb={['Home', 'Quizzes', 'Create']}
        actions={[
          <GlassButton key="cancel" variant="ghost" onClick={() => navigate('/quizzes')}>
            Cancel
          </GlassButton>
        ]}
      />

      <GlassCard variant="default" padding="md" className="step-indicator-card">
        <div className="step-indicator">
          {[1, 2, 3].map(s => (
            <div key={s} className={`step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}>
              <div className="step-number">{s}</div>
              <div className="step-label">
                {s === 1 && 'Basics'}
                {s === 2 && 'Content'}
                {s === 3 && 'Review'}
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      <div className="creator-content">
        {renderStep()}
        {error && (
          <div className="error-message" style={{ color: 'var(--danger)', marginTop: 'var(--space-4)' }}>
            {error}
          </div>
        )}
      </div>

      <GlassCard variant="strong" padding="md" className="creator-footer-card">
        <div className="creator-footer">
          {step > 1 && (
            <GlassButton 
              variant="ghost" 
              onClick={() => setStep(step - 1)} 
              disabled={isGenerating || isSaving}
            >
              Previous
            </GlassButton>
          )}

          <div className="spacer" />

          {step === 1 && (
            <GlassButton
              variant="primary"
              onClick={() => setStep(2)}
              disabled={!formData.project_id || !formData.name}
            >
              Next: Content
            </GlassButton>
          )}

          {step === 3 && (
            <GlassButton
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
            >
              {isSaving ? 'Saving...' : '💾 Save Quiz'}
            </GlassButton>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

export default QuizCreator;
